// Resize-mousedown logic for UmlShapeWidgetBase, extracted (2026-07-09) to keep
// the base class itself small. Pure function operating on a widget instance
// (widget.props, widget._resizeStart, widget.getMinWidth/getMinHeight).
import userSettingsEngine from '../../../controllers/UserSettings/UserSettingsEngine';
import CanvasGeometry from './CanvasGeometry.js';

const CURSORS = {
  nw: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', se: 'nwse-resize',
  n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
};

export { CURSORS };

export function handleResizeMouseDownHelper(widget, handle, e) {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();

  const shape = widget.props.shape;
  // Reference point kept in WORLD units (via toWorldPoint), not raw screen
  // pixels — shape.w/h are world units, so a screen-pixel dx/dy (as this used
  // to compute) silently broke as soon as the canvas was panned or zoomed:
  // the shape resized by less/more than the mouse actually moved, and the
  // resulting shape.w/h no longer matched what the user visually dragged to
  // (bug found 2026-07-10, same root cause as the drag-helper fix — this in
  // turn broke package-containment checks in UmlCanvasEngine, since those
  // compare world coordinates).
  const startWorld = widget.props.toWorldPoint(e.clientX, e.clientY);
  widget._resizeStart = {
    worldX: startWorld.x,
    worldY: startWorld.y,
    w: shape.w,
    h: shape.h,
    shapeX: shape.x,
    shapeY: shape.y,
  };

  document.body.style.userSelect = 'none';
  document.body.classList.add('jx-dragging');
  document.body.style.cursor = CURSORS[handle];

  const { width: minW, height: minH } = widget.getMinSize();

  function onMouseMove(ev) {
    ev.stopImmediatePropagation();
    const worldPoint = widget.props.toWorldPoint(ev.clientX, ev.clientY);
    const dx = worldPoint.x - widget._resizeStart.worldX;
    const dy = worldPoint.y - widget._resizeStart.worldY;

    let newW = widget._resizeStart.w;
    let newH = widget._resizeStart.h;
    let newX = widget._resizeStart.shapeX;
    let newY = widget._resizeStart.shapeY;

    if (handle === 'e' || handle === 'se' || handle === 'ne') newW = widget._resizeStart.w + dx;
    if (handle === 'w' || handle === 'sw' || handle === 'nw') newW = widget._resizeStart.w - dx;
    if (handle === 's' || handle === 'se' || handle === 'sw') newH = widget._resizeStart.h + dy;
    if (handle === 'n' || handle === 'ne' || handle === 'nw') newH = widget._resizeStart.h - dy;

    const snapEnabled = userSettingsEngine.resolve('umlWorkspace.snapToGrid');
    const gridSize = userSettingsEngine.resolve('umlWorkspace.gridSize');
    const clampedW = snapEnabled ? CanvasGeometry.snapToGrid(Math.max(minW, newW), gridSize) : Math.max(minW, newW);
    const clampedH = snapEnabled ? CanvasGeometry.snapToGrid(Math.max(minH, newH), gridSize) : Math.max(minH, newH);

    if (handle === 'w' || handle === 'sw' || handle === 'nw') {
      newX = widget._resizeStart.shapeX + (widget._resizeStart.w - clampedW);
    }
    if (handle === 'n' || handle === 'ne' || handle === 'nw') {
      newY = widget._resizeStart.shapeY + (widget._resizeStart.h - clampedH);
    }

    if (widget.props.onShapeResized) {
      widget.props.onShapeResized(shape.id, clampedW, clampedH);
    }
    if ((newX !== widget._resizeStart.shapeX || newY !== widget._resizeStart.shapeY) && widget.props.onShapeMoved) {
      widget.props.onShapeMoved(shape.id, newX, newY);
    }
  }

  function onMouseUp() {
    document.body.style.userSelect = '';
    document.body.classList.remove('jx-dragging');
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', onMouseMove, true);
    window.removeEventListener('mouseup', onMouseUp, true);
  }

  window.addEventListener('mousemove', onMouseMove, true);
  window.addEventListener('mouseup', onMouseUp, true);
}
